// src/context/AuthContext.tsx

import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "../services/firebase";
import type { User } from "firebase/auth";

type AuthContextType = {
  user: User | null | undefined; // undefined: carregando, null: deslogado
  isAuthenticated: boolean;
  login: (email: string, password: string) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Inicia como 'undefined' para indicar que o estado de auth ainda não foi verificado
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    // onAuthStateChanged retorna uma função de unsubscribe
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser); // Define como o usuário atual ou null
    });

    // Limpa o listener quando o componente desmontar
    return () => unsubscribe();
  }, []);

  const login = (email: string, password: string) => {
    signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        // O onAuthStateChanged vai cuidar de atualizar o estado 'user'
        navigate("/admin/unidades");
      })
      .catch((error) => {
        console.error("Erro no login", error);
        alert("Usuário ou senha digitados incorretamente.");
      });
  };

  const logout = () => {
    signOut(auth).then(() => {
      // O onAuthStateChanged vai cuidar de atualizar o estado 'user' para null
      navigate("/auth/sign-in");
    });
  };

  // isAuthenticated é true se user não for null nem undefined
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
